/**
 * Subscription Usage Panel
 *
 * Collapsible panel showing usage bars for subscription time limits.
 * Displays only limits that are configured (non-null). Each bar shows
 * used / max hours with a visual progress indicator and remaining amount.
 *
 * Title prominence (`titleSize`) is deliberately separate from density (`compact`) — the two
 * consumers pull in opposite directions; see `UsagePanelTitleSize`.
 *
 * Inspired by web-portal-2's OverageDetailsCard usage bars, adapted
 * for the admin portal's drawer context.
 *
 * @module drawer/components/SubscriptionUsagePanel
 */

import { useId, useState } from 'react'
import { buildUsageLimits } from './buildUsageLimits'
import type { UsageLimitsInput, LimitPeriod } from './buildUsageLimits'
import { formatHours } from '@butter/library/shared'
import { cn } from '#utils'
import { ChevronIcon } from '#icons'
import { UsageBar } from '../../components/data-display/UsageBar/UsageBar'

// ============================================================================
// TYPES
// ============================================================================

/**
 * Prominence of the panel's title, independent of `compact`.
 *
 * Density and prominence are separate axes here: the mobile My Plan screen is `compact` yet
 * wants an `xl` title so the panel reads as a peer of the "Plan Details" section beside it,
 * while the admin summary cards are full-density but need `base` so the panel does not
 * outweigh the plan name of the card it belongs to.
 */
export type UsagePanelTitleSize = 'sm' | 'base' | 'lg' | 'xl'

/**
 * Element wrapping the toggle button.
 *
 * `'span'` (the default) leaves the page's heading outline untouched. Pass a heading level
 * when the panel genuinely is a section header — per the APG disclosure pattern the heading
 * wraps the button rather than living inside it, which is also why this is a tag choice and
 * not a slot: `<button>` takes phrasing content only.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/
 */
export type UsagePanelTitleTag = 'h2' | 'h3' | 'h4' | 'span'

const TITLE_SIZE_CLASS: Record<UsagePanelTitleSize, string> = {
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
}

interface SubscriptionUsagePanelProps {
  /** Subscription display name (e.g. "Gold Individual"). Drives the default title; ignored when `title` is set. */
  subscriptionName?: string
  /** Usage stats from the API (include=usage) or booking validation. Ignored when `rows` is set. */
  usage?: UsageLimitsInput
  /** Pre-built rows, bypassing `usage`/`buildUsageLimits` entirely. When set, `usage` is ignored. */
  rows?: readonly UsageLimit[]
  /** Replaces the "{subscriptionName} Usage" title verbatim. */
  title?: string
  /** Start expanded instead of collapsed */
  defaultExpanded?: boolean
  /** Use "Shared X" label variants for team shared usage */
  shared?: boolean
  /** Override color for the usage dot and progress bar fill. Defaults to `#9333ea` (purple-600). Pass `'var(--theme-600)'` from theme-aware contexts. */
  barColor?: string
  /**
   * Bar style for limited rows. `continuous` is a plain rounded track — for
   * cards that already label the figures themselves. `segmented` is the
   * tick-mark row with a leading clock icon (the lesson-pack frame).
   * @default 'continuous'
   */
  barVariant?: UsageBarVariant
  /** Compact mode — tighter spacing, 12 px text, smaller dot/tag. Used by mobile-app's My Plan screen. */
  compact?: boolean
  /** Title prominence. Independent of `compact` — see `UsagePanelTitleSize`. @default 'xl' */
  titleSize?: UsagePanelTitleSize
  /** Element wrapping the toggle button — see `UsagePanelTitleTag`. @default 'span' */
  titleAs?: UsagePanelTitleTag
  /** `false` renders the rows card only — no title/toggle, always open. For a card that is itself the summary (web lesson confirmation). @default true */
  collapsible?: boolean
  /** Merged onto the root element. */
  className?: string
}

/**
 * A capped pool, ready to render. Values are unit-agnostic — `used`/`total` drive the
 * bar's geometry (minutes for the hour pools, slot counts for sessions) and the
 * `*Label` strings carry the already-formatted text, so this component never has
 * to know which unit it is showing.
 */
export interface LimitedUsageLimit {
  /** Discriminant — absent or `false` means capped (has a bar and a "remaining" tag). */
  unlimited?: false
  label: string
  used: number
  total: number
  usedLabel: string
  totalLabel: string
  remainingLabel: string
}

/** An uncapped pool — label + dot + "Unlimited". No bar, no tag. */
export interface UnlimitedUsageLimit {
  unlimited: true
  label: string
}

/** A row ready to render — either a capped pool or an unlimited one. */
export type UsageLimit = LimitedUsageLimit | UnlimitedUsageLimit

/** `continuous` (default): plain track, no icon. `segmented`: clock icon + tick marks. */
export type UsageBarVariant = 'continuous' | 'segmented'

// ============================================================================
// HELPERS
// ============================================================================

// ============================================================================
// USAGE BAR
// ============================================================================

interface UsageLimitDisplayProps {
  limit: UsageLimit
  barColor: string
  barVariant: UsageBarVariant
  compact?: boolean
}

function UsageLimitDisplay({ limit, barColor, barVariant, compact = false }: UsageLimitDisplayProps) {
  const dotClass = compact ? 'inline-block size-1.5 rounded-full' : 'inline-block h-2 w-2 rounded-full'
  const dotTextGap = compact ? 'gap-1' : 'gap-1.5'
  const rightClusterGap = compact ? 'gap-2.5' : 'gap-3'
  const rightClusterText = compact ? 'text-xs' : 'text-sm'
  // Non-compact leads with the label — at text-xs it read smaller than the
  // figures beside it, inverting the hierarchy. Compact stays dense.
  const labelText = compact ? 'text-xs' : 'text-base'
  // Frame gap between rows is 12px compact, unchanged 8px otherwise.
  const rowGap = compact ? 'space-y-3' : 'space-y-2'
  const usedTextClass = `flex items-center ${dotTextGap} ${rightClusterText} font-normal text-butter-gray-700${compact ? ' leading-none' : ''}`

  if (limit.unlimited) {
    return (
      <div className={rowGap}>
        <div className="flex items-center justify-between">
          <span className={`${labelText} font-medium text-butter-gray-800`}>{limit.label}</span>
          <span className={usedTextClass}>
            <span className={dotClass} style={{ backgroundColor: barColor }} />
            Unlimited
          </span>
        </div>
      </div>
    )
  }

  const { label, used, total, usedLabel, totalLabel, remainingLabel } = limit
  const tagClass = compact
    ? 'rounded-full bg-butter-gray-50 px-2 py-1 text-xs font-medium leading-none text-butter-gray-800'
    : 'rounded-full bg-butter-gray-50 px-2 py-1 text-sm font-medium text-butter-gray-800'

  return (
    <div className={rowGap}>
      {/* Label row */}
      <div className="flex items-center justify-between">
        <span className={`${labelText} font-medium text-butter-gray-800`}>{label}</span>
        <div className={`flex items-center ${rightClusterGap} ${rightClusterText}`}>
          <span className={usedTextClass}>
            <span className={dotClass} style={{ backgroundColor: barColor }} />
            {usedLabel} / {totalLabel} used
          </span>
          <span className={tagClass}>
            {remainingLabel} left
          </span>
        </div>
      </div>

      {/* Bar variant is caller-chosen: `continuous` (default) is a plain track
          since the row above already labels the figures; `segmented` (the
          clock icon + ticks) always renders at full, non-compact size even
          inside a compact panel, matching the lesson-pack frame. */}
      <UsageBar
        used={used}
        thisBooking={0}
        total={total}
        borderless
        usedColor={barColor}
        compact={compact && barVariant === 'continuous'}
        variant={barVariant}
      />
    </div>
  )
}

// ============================================================================
// PANEL
// ============================================================================

export function SubscriptionUsagePanel({
  subscriptionName,
  usage,
  rows,
  title,
  defaultExpanded = false,
  shared = false,
  barColor = '#9333ea',
  barVariant = 'continuous',
  compact = false,
  titleSize = 'xl',
  titleAs: Title = 'span',
  collapsible = true,
  className,
}: SubscriptionUsagePanelProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const panelId = useId()

  // Map period → label (varies by `shared` prop for team memberships)
  const labelMap: Record<LimitPeriod, string> = shared
    ? {
        active: 'Shared Active Hours',
        day: 'Shared Daily Hours',
        week: 'Shared Weekly Hours',
        month: 'Shared Monthly Hours',
        activeSessions: 'Shared Active Sessions',
      }
    : {
        active: 'Max Active Hours',
        day: 'Hours Today',
        week: 'Weekly Hours',
        month: 'Monthly Hours',
        activeSessions: 'Max Active Sessions',
      }

  const mapUsageToRows = (input: UsageLimitsInput): LimitedUsageLimit[] =>
    buildUsageLimits(input).map((row) =>
      row.unit === 'sessions'
        ? {
            label: labelMap[row.period],
            used: row.usedSessions,
            total: row.maxSessions,
            usedLabel: String(row.usedSessions),
            totalLabel: String(row.maxSessions),
            remainingLabel: String(row.remainingSessions ?? 0),
          }
        : {
            label: labelMap[row.period],
            used: row.usedMinutes,
            total: row.maxMinutes,
            usedLabel: formatHours(row.usedHours),
            totalLabel: formatHours(row.maxHours),
            remainingLabel: formatHours(row.remainingHours ?? 0),
          },
    )

  // Pre-built rows take priority over `usage`; `usage` is unused when set.
  const activeLimits: readonly UsageLimit[] = rows ?? (usage ? mapUsageToRows(usage) : [])

  // Nothing to show if all limits are unlimited
  if (activeLimits.length === 0) return null

  const titleText = title ?? (subscriptionName ? `${subscriptionName} Usage` : 'Usage')

  // No title/toggle — the rows card is itself the summary (web lesson confirmation).
  if (collapsible === false) {
    return (
      <div
        className={
          compact
            ? 'space-y-3 rounded-20 border border-butter-gray-100 bg-white p-4'
            : 'space-y-4 rounded-2xl border border-butter-gray-100 bg-white p-4'
        }
        aria-label={titleText}
      >
        {activeLimits.map((limit) => (
          <UsageLimitDisplay key={limit.label} limit={limit} barColor={barColor} barVariant={barVariant} compact={compact} />
        ))}
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Heading wraps the toggle rather than containing it — see `UsagePanelTitleTag`. */}
      <Title className="block">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between py-2 text-left"
          aria-expanded={isExpanded}
          aria-controls={`${panelId}-usage-details`}
        >
          <span className={cn(TITLE_SIZE_CLASS[titleSize], 'font-semibold leading-none text-butter-gray-800')}>
            {titleText}
          </span>
          {/* State is announced via aria-expanded on the button, so the
              chevron carries it visually without a redundant label.
              Compact matches the frame's 18px thin chevron (Figma 14621:527444,
              "Icon set/arrow up"); non-compact keeps the admin drawer's existing
              glyph untouched. */}
          {compact ? (
            <ChevronIcon
              aria-hidden
              strokeWidth={1.25}
              className={`size-4.5 shrink-0 text-butter-gray-900 transition-transform ${isExpanded ? '-rotate-90' : 'rotate-90'}`}
            />
          ) : (
            <svg
              className={`size-4 shrink-0 text-gray-900 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 6l4 4 4-4" />
            </svg>
          )}
        </button>
      </Title>

      {/* Collapsible content */}
      {isExpanded && (
        <div
          id={`${panelId}-usage-details`}
          className={
            compact
              ? 'mt-4 space-y-3 rounded-20 border border-butter-gray-100 bg-white p-4'
              : 'mt-3 space-y-4 rounded-2xl border border-butter-gray-100 bg-white p-4'
          }
        >
          {activeLimits.map((limit) => (
            <UsageLimitDisplay
              key={limit.label}
              limit={limit}
              barColor={barColor}
              barVariant={barVariant}
              compact={compact}
            />
          ))}
        </div>
      )}
    </div>
  )
}
